// pages/BlogList.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Sort,
  Visibility,
  Edit,
  Delete,
  Add,
  MoreVert,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { collection, query, orderBy, getDocs, deleteDoc, doc, } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { deleteFileByUrl } from '../../lib/githubUpload';

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const blogsRef = collection(db, 'blogs');
      const q = query(blogsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const blogsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      }));
      setBlogs(blogsData);
    } catch (err) {
      setError('Failed to load blogs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blog) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        // Extract and delete images from GitHub
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(blog.content)) !== null) {
          await deleteFileByUrl(match[1]);
        }

        // Delete from Firestore
        await deleteDoc(doc(db, 'blogs', blog.id));
        
        // Refresh list
        await fetchBlogs();
        alert('Blog deleted successfully');
      } catch (err) {
        console.error('Error deleting blog:', err);
        alert('Failed to delete blog');
      }
    }
    handleMenuClose();
  };

  const handleMenuOpen = (event, blog) => {
    setAnchorEl(event.currentTarget);
    setSelectedBlog(blog);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBlog(null);
  };

  const filteredBlogs = blogs.filter(blog =>
    blog.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedBlogs = [...filteredBlogs].sort((a, b) => {
    if (sortBy === 'newest') return b.createdAt - a.createdAt;
    if (sortBy === 'oldest') return a.createdAt - b.createdAt;
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    return 0;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Blog Posts
        </Typography>
        {currentUser && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/blogs/create')}
          >
            Create Blog
          </Button>
        )}
      </Box>

      <Box display="flex" gap={2} mb={4}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search blogs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<Sort />}
          onClick={() => setSortBy(sortBy === 'newest' ? 'oldest' : sortBy === 'oldest' ? 'title' : 'newest')}
        >
          {sortBy === 'newest' ? 'Newest First' : sortBy === 'oldest' ? 'Oldest First' : 'By Title'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {sortedBlogs.map((blog) => (
          <Grid item xs={12} md={6} lg={4} key={blog.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {blog.coverImage && (
                <Box
                  component="img"
                  src={blog.coverImage}
                  alt={blog.title}
                  sx={{
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Chip
                    label={blog.category || 'General'}
                    size="small"
                    color="primary"
                  />
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, blog)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="h6" component="h2" gutterBottom>
                  {blog.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {blog.excerpt || blog.content?.substring(0, 150)}...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {blog.createdAt?.toLocaleDateString()} • By {blog.author || 'Anonymous'}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => navigate(`/admin/blogs/${blog.id}`)}
                >
                  Read More
                </Button>
                {currentUser && currentUser.uid === blog.userId && (
                  <>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => navigate(`/admin/blogs/edit/${blog.id}`)}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {sortedBlogs.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No blogs found
          </Typography>
        </Box>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => navigate(`/admin/blogs/${selectedBlog?.id}`)}>
          <Visibility sx={{ mr: 1 }} /> View
        </MenuItem>
        {currentUser && currentUser.uid === selectedBlog?.userId && (
          <>
            <MenuItem onClick={() => navigate(`/admin/blogs/edit/${selectedBlog?.id}`)}>
              <Edit sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem onClick={() => handleDelete(selectedBlog)} sx={{ color: 'error.main' }}>
              <Delete sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Container>
  );
};

export default BlogList;